import { useState } from 'react';
import { Stack, Group, Text, Badge, Button, ActionIcon, Modal, CopyButton, Center, Badge as MantineBadge, Tooltip, Table, Menu } from '@mantine/core';
import { IconCopy, IconEye, IconCheck, IconTrash, IconDots } from '@tabler/icons-react';
import MilkdownRenderer from '../shared/MilkdownRenderer';
import { JobArtifact, usePublishArtifact, useDeleteArtifact } from '../../api/artifacts';
import { ResumeContent } from '../../api/resumes';

interface ArtifactVersionsListProps {
  jobId?: string;
  artifactType: string;
  artifacts: JobArtifact[];
  contentItems: ResumeContent[];
  onArtifactUpdated: () => void;
}

export default function ArtifactVersionsList({
  jobId,
  artifactType,
  artifacts,
  contentItems,
  onArtifactUpdated,
}: ArtifactVersionsListProps) {
  // Filter artifacts by type
  const typeArtifacts = artifacts.filter((a) => a.artifact_type === artifactType);

  if (typeArtifacts.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Text c="dimmed">No {artifactType} artifacts created yet</Text>
          <Text size="sm" c="dimmed">
            Use the compiler above to create your first {artifactType}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Version</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Used Content</Table.Th>
          <Table.Th>Created</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {typeArtifacts.map((artifact) => (
          <ArtifactRow
            key={artifact.id}
            artifact={artifact}
            contentItems={contentItems}
            onArtifactUpdated={onArtifactUpdated}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
}

interface ArtifactRowProps {
  artifact: JobArtifact;
  contentItems: ResumeContent[];
  onArtifactUpdated: () => void;
}

function ArtifactRow({ artifact, contentItems, onArtifactUpdated }: ArtifactRowProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const publishMutation = usePublishArtifact(artifact.id);
  const deleteMutation = useDeleteArtifact(artifact.id);

  // Get names of used content (with fallback for old data without used_content_ids)
  const usedContentNames = (artifact.used_content_ids || [])
    .map((id) => contentItems.find((item) => item.id === id)?.name)
    .filter(Boolean);

  const handlePublish = async () => {
    try {
      await publishMutation.mutateAsync();
      onArtifactUpdated();
    } catch (error) {
      console.error('Failed to publish artifact:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync();
      setDeleteModalOpen(false);
      onArtifactUpdated();
    } catch (error) {
      console.error('Failed to delete artifact:', error);
    }
  };

  const isProcessing = publishMutation.isPending || deleteMutation.isPending;

  const createdDate = new Date(artifact.created_at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Text
            fw={500}
            onClick={() => setPreviewOpen(true)}
            style={{ cursor: 'pointer' }}
          >
            {artifact.name}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">v{artifact.version}</Text>
        </Table.Td>
        <Table.Td>
          <MantineBadge size="sm" variant="light" color={artifact.status === 'published' ? 'green' : 'gray'}>
            {artifact.status === 'published' ? '✓ Published' : 'Draft'}
          </MantineBadge>
        </Table.Td>
        <Table.Td>
          {usedContentNames.length > 0 ? (
            <Group gap="xs">
              {usedContentNames.map((name, i) => (
                <Badge key={i} size="sm" variant="light">
                  {name}
                </Badge>
              ))}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{createdDate}</Text>
        </Table.Td>
        <Table.Td>
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye size={14} />}
                onClick={() => setPreviewOpen(true)}
                disabled={isProcessing}
              >
                Preview
              </Menu.Item>
              <CopyButton value={artifact.content_markdown} timeout={2000}>
                {({ copied: isCopied, copy }) => (
                  <Menu.Item
                    leftSection={isCopied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    onClick={copy}
                  >
                    {isCopied ? 'Copied!' : 'Copy Markdown'}
                  </Menu.Item>
                )}
              </CopyButton>
              <Menu.Item
                leftSection={artifact.status === 'published' ? <IconCheck size={14} /> : <IconCheck size={14} />}
                onClick={handlePublish}
                disabled={isProcessing}
              >
                {artifact.status === 'published' ? 'Unpublish' : 'Publish'}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => setDeleteModalOpen(true)}
                disabled={isProcessing}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>

      {/* Preview Modal */}
      <Modal opened={previewOpen} onClose={() => setPreviewOpen(false)} title={`Preview: ${artifact.name}`} size="xl">
        <MilkdownRenderer content={artifact.content_markdown} />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete Artifact" centered>
        <Stack gap="md">
          <Text>Are you sure you want to delete this {artifact.artifact_type} artifact? This action cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteModalOpen(false)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteMutation.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
